import { CurrencyPipe } from '@angular/common';
import {
  Component,
  computed,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  Renderer2,
  ViewEncapsulation
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { AccountService, QuoteOrdersService, SampleOrderService } from '@coreApiServices';
import { CommonConstants, ROUTES, STORAGE } from '@coreConstant';
import { GoogleAddressModel, RequestParamModel, UserModel } from '@coreModels';
import {
  ConfirmModalService,
  LoaderService,
  SubscribersService,
  ToastService,
  TokenStorageService,
  UtilityService
} from '@coreServices';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { forkJoin } from 'rxjs';
import { HandleImageErrorDirective } from "../../../../directives/handle-image-error.directive";
import { Loader } from '@googlemaps/js-api-loader';
import { environment } from 'src/environments/environment';
import { NgxGpAutocompleteModule } from '@angular-magic/ngx-gp-autocomplete';
import { ModalWaSampleRequestRestrictionComponent } from '../../modals/modal-wa-sample-request-restriction/modal-wa-sample-request-restriction.component';

@Component({
  selector: 'app-sample-order',
  standalone: true,
  imports: [RouterLink, CurrencyPipe, ReactiveFormsModule, HandleImageErrorDirective, NgxGpAutocompleteModule],
  templateUrl: './sample-order.component.html',
  styleUrl: './sample-order.component.scss',
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: Loader,
      useValue: new Loader({
        apiKey: environment.GOOGLE_MAP_KEY,
        libraries: ['places']
      })
    },
  ],
})
export class SampleOrderComponent {
  @Output() closeEvent = new EventEmitter();
  @Input() productModel: any;
  @Input() isModal: boolean = false;
  ROUTES = ROUTES;
  isLoading = false;
  sampleOrders: any = computed(() => this.subscriberService.sampleOrders());
  // progressbarWidth = computed(() => this.sampleOrders().length > 0 ? ((100 * this.sampleOrders().length) / 6) : 0);
  progressbarWidth = computed(() => this.sampleOrders().length > 0 ? this.sampleOrders().length : 0);
  s3BucketImageURL = CommonConstants.IMAGE_KIT_ENDPOINT;
  IMAGE_SIZE_69_X_69 = CommonConstants.IMAGE_SIZE_69_X_69;
  exclusiveGST = CommonConstants.TAX_TYPE.EXCLUSIVE_GST;
  sampleProductPrice = 0;
  private unListen: (() => void) | undefined;
  cartQuoteForm!: FormGroup;
  userModel!: UserModel;
  isCheckout: boolean = false;
  options = {
    componentRestrictions: { country: 'AU' },
    strictBounds: true,
    fields: ['ALL'],
    types: ['address'],
  };
  invalidPostalCode: boolean = false;
  steps = [1,2,3,4,5,6];

  constructor(private tokenStorageService: TokenStorageService, public utilityService: UtilityService, private subscriberService: SubscribersService, private confirmModalService: ConfirmModalService, private el: ElementRef, private renderer: Renderer2, private fb: FormBuilder, private loaderService: LoaderService, private quoteOrdersService: QuoteOrdersService, private toastService: ToastService, private router: Router, public modalService: NgbModal, private sampleOrderService: SampleOrderService, private accountService: AccountService) {
  }

  ngOnInit() {
    // this.onSidebarBodyScroll();
    this.userModel = this.tokenStorageService.getUser();
    this.cartQuoteForm = this.fb.group({
      project_name: ['', [Validators.required]],
      description: [''],
      site_contact_name: ['', [Validators.required]],
      site_contact_number: ['', [Validators.required, Validators.minLength(10), Validators.maxLength(10), this.mobileValidator.bind(this), Validators.pattern('^[0-9]{10}$')]],
      site_contact_email: ['', [Validators.required, Validators.email]],
      delivery_address_line_1: ['', [Validators.required, this.addressValidator.bind(this)]],
      delivery_address_line_2: [''],
      delivery_suburb: ['', Validators.required],
      delivery_town_city: ['', Validators.required],
      delivery_state_region: ['', Validators.required],
      delivery_post_code: ['', Validators.required],
      delivery_country: ['', Validators.required],
    });
    this.setRecipientDetail();
    this.getAddresses();
  }

  get f() {
    return this.cartQuoteForm.controls;
  }

  private mobileValidator(control: FormControl): { [s: string]: boolean } {
    if (this.cartQuoteForm && control.value) {
      let $mobileNumber = control.value;
      if ($mobileNumber.substring(0, 2) == '04') {
        return {};
      }
    }
    return { mobileNotValid: true };
  }

  private addressValidator(control: FormControl): { [s: string]: boolean } {
    let addressRegex = CommonConstants.PO_BOX_ADDRESS_REGEX;

    if (this.cartQuoteForm && control.value) {
      if (!addressRegex.test(control.value)) {
        return {};
      }
    }
    return { addressNotValid: true };
  }

  restrictNonNumeric(event: KeyboardEvent) {
    const allowedKeys = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete'];
    if (allowedKeys.includes(event.key)) {
      return;
    }
    if (!/^\d$/.test(event.key)) {
      event.preventDefault();
    }
  }

  async handleAddressChange(address?: any) {
    this.setFormValues();
    if (address && address.formatted_address) {
      let addressObject = this.utilityService.addressHandler(address) as GoogleAddressModel;
      if (addressObject) {
        this.setFormValues(addressObject);
        if (!addressObject.isStreetAddress) {
          this.cartQuoteForm.controls['delivery_address_line_1'].setErrors({ streetRequired: CommonConstants.STREET_NO_REQUIRED })
        }
      }
    } else {
      this.cartQuoteForm.controls['delivery_address_line_1'].setValue(this.cartQuoteForm.value.address_line_1);
    }
  }

  getAddresses() {
    if (this.userModel && this.userModel.id) {
      this.loaderService.show();
      const requestParams = new RequestParamModel();
      requestParams.expands = ['frontendUserAddresses.fullAddress', 'company', 'company.defaultAddress', 'company.companyAddresses'];
      delete requestParams.pageSize;

      this.accountService.getContactDetail(this.userModel.id, requestParams).subscribe({
        next: (userModel: UserModel) => {
          if (userModel?.company?.defaultAddress) {
            this.setFormValues(userModel.company.defaultAddress);
            this.loaderService.hide();
          } else if (userModel?.company?.companyAddresses?.length > 0) {
            this.setFormValues(userModel.company.companyAddresses[0]);
            this.loaderService.hide();
          }
        },
        error: (error: any) => {
          this.toastService.showError(error.error.data.message);
          this.loaderService.hide();
        },
      });
    }
  }

  setRecipientDetail() {
    this.cartQuoteForm.patchValue({
      site_contact_name: this.userModel.first_name + ' ' + this.userModel.last_name,
      site_contact_number: this.userModel.mobile,
      site_contact_email: this.userModel.email,
    });
  }

  setFormValues(addressObject?: any) {
    this.cartQuoteForm.patchValue({
      delivery_address_line_1: addressObject ? addressObject.address_line_1 : '',
      delivery_address_line_2: addressObject ? addressObject.address_line_2 : '',
      delivery_suburb: addressObject ? addressObject.suburb : '',
      delivery_town_city: addressObject ? addressObject.town_city : '',
      delivery_state_region: addressObject ? addressObject.state_region : '',
      delivery_post_code: addressObject ? addressObject.post_code : '',
      delivery_country: addressObject ? addressObject.country : ''
    })
    this.checkPostalCode()
  }

  checkPostalCode() {
    if (this.cartQuoteForm.value.delivery_post_code.startsWith(CommonConstants.WA_POSTAL_CODE_PREFIX)) {
      let modalRef = this.modalService.open(ModalWaSampleRequestRestrictionComponent, {
        centered: true,
        windowClass: '',
        backdrop: 'static',
      });
      modalRef.closed.subscribe((result: any) => {
        if (result) {
          this.invalidPostalCode = true;
          this.setFormValues();
        }
      });
    } else {
      this.invalidPostalCode = false;
    }
  }

  removeProduct(id: any) {
    this.confirmModalService.confirm(CommonConstants.CONFIRMATION_CART_PRODUCT_DELETE_TITLE, CommonConstants.CONFIRMATION_CART_PRODUCT_DELETE_MESSAGE).then((confirmed) => {
      if (confirmed) {
        this.subscriberService.sampleOrders.update((orders: any) => orders.filter((o: any) => o.id !== id));
        this.tokenStorageService.setSampleOrder(this.subscriberService.sampleOrders());
      }
    });

  }

  onSidebarBodyScroll() {
    const scrollableDiv = this.el.nativeElement.querySelector('.sample-order-list');
    this.unListen = this.renderer?.listen(scrollableDiv, 'wheel', (event: WheelEvent) => {
      const { scrollTop, scrollHeight, clientHeight } = scrollableDiv;
      const atTop = scrollTop === 0 && event.deltaY < 0;
      const atBottom = scrollTop + clientHeight === scrollHeight && event.deltaY > 0;
      if (atTop || atBottom) {
        event.preventDefault();
      }
    });
  }

  onSubmitQuote() {
    if (this.cartQuoteForm.invalid) {
      this.cartQuoteForm.markAllAsTouched();
      return;
    }
    let text = 'Although we’d love to offer a re-collection service, considerable logistical hurdles make this unfeasible. We’ve been particularly conscious to only apply landfill biodegradable packaging and would kindly request that you thoughtfully repurpose, gift, or dispose of any samples that become redundant.'
    this.confirmModalService.confirm('Premium samples are our gift to you.', text, null, 'Accept')
      .then(async (confirmed) => {
        if (confirmed) {
          this.loaderService.show();
          let quoteForm = {
            frontend_user_id: this.userModel.id,
            type: CommonConstants.QUOTE_TYPE_SAMPLE_ORDER,
            ...this.cartQuoteForm.value
          }
          let sample: any = await this.sampleOrderService.getSampleOrders(this.userModel.id, true);
          if (sample?.error || this.subscriberService.sampleOrderError()) {
            return;
          }
          this.quoteOrdersService.saveQuoteOrder(quoteForm).subscribe({
            next: (quoteId: any) => {
              let length = this.sampleOrders().length;
              if (length > 0) {
                const observables = this.sampleOrders().map((element: any) => {
                  let quoteOrderProduct: any = {
                    quote_id: quoteId,
                    product_id: element.id,
                    quantity: element?.sampleProduct?.moi || 1,
                  };
                  return this.quoteOrdersService.saveQuoteProduct(quoteOrderProduct);
                });
                forkJoin(observables).subscribe({
                  next: () => {
                    this.afterSampleOrderConfirmation(quoteId);
                  },
                  error: (err: any) => {
                    this.loaderService.hide();
                  },
                  complete: () => {
                    this.loaderService.hide();
                  }
                });
              }
            },
            error: (error) => {
              this.loaderService.hide();
              this.toastService.showError(error);
            },
          })
        } else {
          this.loaderService.hide();
        }
      });
  }

  afterSampleOrderConfirmation(quoteId: any) {
    this.loaderService.hide();
    this.toastService.showSuccess(CommonConstants.SAMPLE_CREATED_SUCCESS_MESSAGE);
    this.tokenStorageService.removeItemStorage(STORAGE.SAMPLE_ORDER_KEY);
    this.subscriberService.sampleOrders.set([]);
    this.cartQuoteForm.reset();
    this.modalService?.dismissAll();
    this.router.navigate(['/' + ROUTES.ACCOUNT.ROOT + '/' + ROUTES.ACCOUNT.SAMPLES + '/' + quoteId]);
  }

  scrollToTop() {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }

  ngOnDestroy(): void {
    if (this.unListen) {
      this.unListen();
    }
  }
}
